import React, { useState, useEffect, useRef } from 'react';
import {
    Users, UserX, UserMinus, UserCheck, Search, ChevronLeft, ChevronRight,
    Download, ChevronDown, Check, Eye, FileText, TrendingUp,
    Repeat, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink
} from 'lucide-react';
import { CORES, Botao, CartaoKpi, estilos } from '../psUI';

// Quantos dias no funil sem chegar ao fim já contam como "parado" (radar de gargalo).
const LIMITE_PARADO_DIAS = 15;

// ==========================================
// COMPONENTE DE DROPDOWN CUSTOMIZADO
// ==========================================
const DropdownCustomizado = ({ value, onChange, dados, placeholder, minWidth, flex }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    let textoSelecionado = placeholder;
    dados.forEach(bloco => {
        if (bloco.isGeral && bloco.value === value) textoSelecionado = bloco.label;
        if (bloco.items) {
            const achou = bloco.items.find(i => i.value === value);
            if (achou) textoSelecionado = achou.label;
        }
    });

    return (
        <div ref={wrapperRef} style={{ position: 'relative', flex: flex || '1', minWidth: minWidth || '250px' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc', cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', height: '100%',
                    boxSizing: 'border-box', transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '14px', color: value ? '#0f172a' : '#64748b', fontWeight: '500' }}>
                    {textoSelecionado}
                </span>
                <ChevronDown size={18} color="#64748b" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginLeft: '8px' }} />
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                    backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                    zIndex: 50, maxHeight: '350px', overflowY: 'auto'
                }}>
                    {dados.map((bloco, bIdx) => (
                        <div key={bIdx}>
                            {bloco.isGeral && (
                                <div
                                    onClick={() => { onChange(bloco.value); setIsOpen(false); }}
                                    style={{
                                        padding: '12px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                                        backgroundColor: value === bloco.value ? '#ffe4e6' : '#fff',
                                        color: value === bloco.value ? '#e11d48' : '#475569',
                                        borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = value === bloco.value ? '#ffe4e6' : '#f8fafc'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = value === bloco.value ? '#ffe4e6' : '#fff'}
                                >
                                    {bloco.label}
                                    {value === bloco.value && <Check size={16} color="#e11d48" />}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
const ProcessoSeletivoMonitoramento = ({ irParaAba }) => {
    const [candidatos, setCandidatos] = useState([]);
    const [loading, setLoading] = useState(true);
    // Métricas do funil (vêm do /metricas — mesma fonte que alimentava a antiga Visão Geral)
    const [metricas, setMetricas] = useState({ visitas_vagas: 0, visitas_inscricao: 0, total_inscricoes: 0, total_reprovados: 0, taxa_conversao: 0, taxa_reprovacao: 0 });

    // Filtro de categoria (chips). Toggles de controle independentes.
    const [filtroStatus, setFiltroStatus] = useState('TODOS');
    const [filtroReincidente, setFiltroReincidente] = useState(false);
    const [filtroFuncAtual, setFiltroFuncAtual] = useState(false);
    const [filtroExFunc, setFiltroExFunc] = useState(false);
    const [filtroParado, setFiltroParado] = useState(false);

    const [buscaNome, setBuscaNome] = useState('');
    const [buscaVaga, setBuscaVaga] = useState('');
    const [filtroOrigem, setFiltroOrigem] = useState(''); // '' | 'PUBLICA' | 'INTERNA'
    const [filtroMes, setFiltroMes] = useState(''); // '' (todos) | 'AAAA-MM' — recorte pela data de inscrição

    const [ordenacao, setOrdenacao] = useState({ chave: null, direcao: 'asc' });
    const [paginaAtual, setPaginaAtual] = useState(1);
    const ITENS_POR_PAGINA = 50;

    useEffect(() => {
        carregarCandidatos();
    }, []);

    // KPIs do topo acompanham o filtro de período (o /metricas aceita ?mes=AAAA-MM).
    // No mount roda com '' (base inteira) — por isso não há carregarMetricas() no efeito acima.
    useEffect(() => {
        carregarMetricas(filtroMes);
    }, [filtroMes]);

    useEffect(() => {
        setPaginaAtual(1);
    }, [filtroStatus, filtroReincidente, filtroFuncAtual, filtroExFunc, filtroParado, filtroOrigem, filtroMes, buscaNome, buscaVaga, ordenacao]);

    const carregarMetricas = async (mes = '') => {
        try {
            const token = localStorage.getItem('authToken');
            const url = `/api/processo-seletivo/metricas${mes ? `?mes=${mes}` : ''}`;
            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (response.ok) setMetricas(await response.json());
        } catch (error) { console.error("Erro ao buscar métricas:", error); }
    };

    const carregarCandidatos = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch('/api/processo-seletivo/candidatos', { headers: { 'Authorization': `Bearer ${token}` } });

            if (response.ok) {
                const data = await response.json();

                // Dedup por CPF (mesma regra usada no Banco de Talentos): prefere a inscrição
                // não-arquivada (status ≠ 99); se só há a de Processo, usa ela.
                const candidatosMap = new Map();
                data.forEach(cand => {
                    const cpf = cand.CPF;
                    const existente = candidatosMap.get(cpf);
                    if (!existente) {
                        candidatosMap.set(cpf, cand);
                    } else if (cand.ORIGEM === 'Inscricao' && Number(cand.STATUS_ETAPA) !== 99) {
                        candidatosMap.set(cpf, cand);
                    } else if (cand.ORIGEM === 'Processo' && existente.ORIGEM === 'Inscricao' && Number(existente.STATUS_ETAPA) === 99) {
                        candidatosMap.set(cpf, cand);
                    }
                });

                setCandidatos(Array.from(candidatosMap.values()));
            }
        } catch (error) {
            console.error("Erro ao buscar candidatos:", error);
        } finally {
            setLoading(false);
        }
    };

    // Categoria ampla — MESMA lógica do Banco de Talentos (fonte da verdade do módulo).
    const getStatusCategoria = (cand) => {
        const origem = cand.ORIGEM;
        const status = Number(cand.STATUS_ETAPA);
        if (origem === 'Inscricao') {
            if (status === 0) return 'REPROVADOS';
            if (status === 1) return 'ATIVOS';
            if (status === 3) {
                const motivo = (cand.MOTIVO_REPROVACAO || '').toUpperCase();
                if (motivo.includes('DESIST') || motivo.includes('COMPAREC')) return 'DESISTENTES';
                return 'REPROVADOS';
            }
        } else {
            if (status === 0) {
                const motivo = (cand.MOTIVO_REPROVACAO || '').toUpperCase();
                if (motivo.includes('DESIST') || motivo.includes('COMPAREC')) return 'DESISTENTES';
                return 'REPROVADOS';
            }
            if (status === 8) return 'DESISTENTES';
            if (status === 7) return 'APROVADOS';
            if (status >= 2 && status <= 6) return 'ATIVOS';
        }
        return 'ATIVOS';
    };

    // Status detalhado — rótulos curtos e corretos por etapa (antes eram longos e ambíguos).
    const getStatusDisplay = (cand) => {
        const origem = cand.ORIGEM;
        const status = Number(cand.STATUS_ETAPA);
        const motivo = (cand.MOTIVO_REPROVACAO || '').toUpperCase();
        const ehDesist = motivo.includes('DESIST') || motivo.includes('COMPAREC');

        const RED = { cor: '#be123c', bg: '#fff1f2' };
        const AMBER = { cor: '#b45309', bg: '#fffbeb' };
        const BLUE = { cor: '#1d4ed8', bg: '#eff6ff' };
        const GREEN = { cor: '#047857', bg: '#ecfdf5' };
        const PURPLE = { cor: '#6d28d9', bg: '#f5f3ff' };

        if (origem === 'Inscricao') {
            if (status === 0) return { texto: 'Reprovado (Triagem)', ...RED };
            if (status === 1) return { texto: 'Inscrito (Triagem)', ...PURPLE };
            if (status === 3) return ehDesist ? { texto: 'Desistência', ...AMBER } : { texto: 'Reprovado', ...RED };
            if (status === 99) return { texto: 'Em Processo', ...BLUE };
        } else {
            if (status === 0) return ehDesist ? { texto: 'Desistência', ...AMBER } : { texto: 'Reprovado (Processo)', ...RED };
            if (status === 2) return { texto: 'Fila de Entrevista', ...BLUE };
            if (status === 3) return { texto: 'Entrevista Agendada', ...BLUE };
            if (status === 4) return { texto: 'Exame de Admissão', ...BLUE };
            if (status === 6) return { texto: 'Aprovado (aguard. integração)', ...GREEN };
            if (status === 7) return { texto: 'Contratado', ...GREEN };
            if (status === 8) return { texto: 'Desistente', ...AMBER };
        }
        return { texto: `Etapa ${status}`, cor: '#64748b', bg: '#f1f5f9' };
    };

    // Em qual ABA do módulo este candidato está sendo tratado (para o link rápido).
    const abaDoCandidato = (cand) => {
        const cat = getStatusCategoria(cand);
        if (cat === 'REPROVADOS' || cat === 'DESISTENTES') return 'banco-talentos';
        if (cat === 'APROVADOS') return 'aprovados';
        // Em processo — refina pela etapa
        const status = Number(cand.STATUS_ETAPA);
        if (cand.ORIGEM === 'Inscricao' && status === 1) return 'inscritos';
        if (status === 4 || status === 6) return 'aprovados';
        return 'agendamento'; // fila/entrevista (status 2/3) e demais em processo
    };

    // Helpers de controle
    const diasNoFunil = (cand) => {
        if (!cand.DATA_INSCRICAO) return 0;
        return Math.floor((Date.now() - new Date(cand.DATA_INSCRICAO).getTime()) / 86400000);
    };
    const isReincidente = (cand) => Number(cand.QTD_PARTICIPACOES) > 1;
    // Vínculo do CPF com a folha, calculado no backend ('ATUAL' | 'EX' | null). São situações de
    // recrutamento diferentes — funcionário atual é movimentação interna; ex-funcionário é recontratação.
    const isFuncAtual = (cand) => cand.VINCULO_FOLHA === 'ATUAL';
    const isExFunc = (cand) => cand.VINCULO_FOLHA === 'EX';
    // Chave 'AAAA-MM' da data de inscrição (alimenta o filtro de período) e formatação dd/mm/aaaa.
    const mesDe = (cand) => {
        if (!cand.DATA_INSCRICAO) return '';
        const d = new Date(cand.DATA_INSCRICAO);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const fmtData = (valor) => valor ? new Date(valor).toLocaleDateString('pt-BR') : '—';
    // Origem da inscrição: null/ausente (dado antigo) cai como PUBLICA (mesmo default do backfill).
    const origemDe = (cand) => (cand.ORIGEM_INSCRICAO || 'PUBLICA');
    // "Parado" só faz sentido para quem ainda está no funil (em processo) — não para finalizados.
    const isParado = (cand) => getStatusCategoria(cand) === 'ATIVOS' && diasNoFunil(cand) > LIMITE_PARADO_DIAS;

    const lidarComOrdenacao = (chave) => {
        let direcao = 'asc';
        if (ordenacao.chave === chave && ordenacao.direcao === 'asc') direcao = 'desc';
        setOrdenacao({ chave, direcao });
    };
    const renderizarIconeOrdenacao = (chave) => {
        if (ordenacao.chave !== chave) return <ArrowUpDown size={14} color="#cbd5e1" />;
        if (ordenacao.direcao === 'asc') return <ArrowUp size={14} color="#0f172a" />;
        return <ArrowDown size={14} color="#0f172a" />;
    };

    // Contagens (situação dos candidatos). Respeitam o filtro de PERÍODO — ao escolher um
    // mês, os cards/toggles mostram as quantidades daquele mês (pedido de 2026-07-03).
    // Os demais filtros (busca, vaga, origem...) não entram aqui de propósito: os cards
    // são o "mapa" das categorias dentro do período, não o eco da tabela filtrada.
    const candidatosDoPeriodo = filtroMes ? candidatos.filter(c => mesDe(c) === filtroMes) : candidatos;
    const totalGeral = candidatosDoPeriodo.length;
    const totalAprovados = candidatosDoPeriodo.filter(c => getStatusCategoria(c) === 'APROVADOS').length;
    const totalDesistentes = candidatosDoPeriodo.filter(c => getStatusCategoria(c) === 'DESISTENTES').length;
    const totalReprovados = candidatosDoPeriodo.filter(c => getStatusCategoria(c) === 'REPROVADOS').length;
    const totalEmProcesso = candidatosDoPeriodo.filter(c => getStatusCategoria(c) === 'ATIVOS').length;
    const qtdReincidentes = candidatosDoPeriodo.filter(isReincidente).length;
    const qtdFuncAtual = candidatosDoPeriodo.filter(isFuncAtual).length;
    const qtdExFunc = candidatosDoPeriodo.filter(isExFunc).length;
    const qtdParados = candidatosDoPeriodo.filter(isParado).length;

    const candidatosFiltrados = candidatos.filter(c => {
        if (filtroStatus !== 'TODOS' && getStatusCategoria(c) !== filtroStatus) return false;
        if (filtroReincidente && !isReincidente(c)) return false;
        if (filtroFuncAtual && !isFuncAtual(c)) return false;
        if (filtroExFunc && !isExFunc(c)) return false;
        if (filtroParado && !isParado(c)) return false;
        if (filtroOrigem && origemDe(c) !== filtroOrigem) return false;
        if (filtroMes && mesDe(c) !== filtroMes) return false;
        if (buscaNome && !(c.NOME_COMPLETO || '').toLowerCase().includes(buscaNome.toLowerCase())) return false;
        const vagaDoCandidato = c.CARGO || c.VAGA_SELECIONADA || '';
        if (buscaVaga && vagaDoCandidato !== buscaVaga) return false;
        return true;
    });

    const candidatosOrdenados = [...candidatosFiltrados].sort((a, b) => {
        if (!ordenacao.chave) return 0;
        let valorA = '';
        let valorB = '';
        if (ordenacao.chave === 'NOME') {
            valorA = (a.NOME_COMPLETO || '').toLowerCase();
            valorB = (b.NOME_COMPLETO || '').toLowerCase();
        } else if (ordenacao.chave === 'VAGA') {
            valorA = (a.CARGO || a.VAGA_SELECIONADA || '').toLowerCase();
            valorB = (b.CARGO || b.VAGA_SELECIONADA || '').toLowerCase();
        } else if (ordenacao.chave === 'PARTICIPACOES') {
            valorA = Number(a.QTD_PARTICIPACOES) || 0;
            valorB = Number(b.QTD_PARTICIPACOES) || 0;
        } else if (ordenacao.chave === 'STATUS') {
            valorA = getStatusDisplay(a).texto.toLowerCase();
            valorB = getStatusDisplay(b).texto.toLowerCase();
        } else if (ordenacao.chave === 'INSCRICAO') {
            valorA = a.DATA_INSCRICAO ? new Date(a.DATA_INSCRICAO).getTime() : 0;
            valorB = b.DATA_INSCRICAO ? new Date(b.DATA_INSCRICAO).getTime() : 0;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPaginas = Math.ceil(candidatosOrdenados.length / ITENS_POR_PAGINA);
    const indexUltimo = paginaAtual * ITENS_POR_PAGINA;
    const indexPrimeiro = indexUltimo - ITENS_POR_PAGINA;
    const candidatosPaginados = candidatosOrdenados.slice(indexPrimeiro, indexUltimo);

    const exportarParaCSV = () => {
        if (candidatosOrdenados.length === 0) {
            alert("Não há dados para exportar com os filtros atuais.");
            return;
        }
        const cabecalhos = ['Data de Inscrição', 'Nome', 'Vaga/Cargo', 'Origem', 'Participações', 'Vínculo com a folha', 'Status Detalhado', 'CPF', 'E-mail', 'CEP', 'Data de Nascimento'];
        const linhasCSV = candidatosOrdenados.map(cand => {
            const vaga = cand.CARGO || cand.VAGA_SELECIONADA || 'Não definida';
            const infoStatus = getStatusDisplay(cand);
            const nomeLimpo = cand.NOME_COMPLETO?.replace(/"/g, '""') || '';
            const emailLimpo = cand.EMAIL?.replace(/"/g, '""') || '';
            const cpfLimpo = cand.CPF || '';
            const cepLimpo = cand.CEP || '';
            let dataNascFormatada = '';
            if (cand.DATA_NASCIMENTO) {
                dataNascFormatada = new Date(cand.DATA_NASCIMENTO).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
            }
            const origemTxt = origemDe(cand) === 'INTERNA' ? 'Interna (RH)' : 'Pública (site)';
            const dataInscFormatada = cand.DATA_INSCRICAO ? fmtData(cand.DATA_INSCRICAO) : '';
            // Três estados — o "Sim/Não" antigo escondia o ex-funcionário dentro do "Sim".
            const vinculoTxt = isFuncAtual(cand) ? 'Funcionário atual' : isExFunc(cand) ? 'Ex-funcionário' : 'Nunca trabalhou aqui';
            return `"${dataInscFormatada}";"${nomeLimpo}";"${vaga}";"${origemTxt}";"${Number(cand.QTD_PARTICIPACOES) || 1}";"${vinculoTxt}";"${infoStatus.texto}";"${cpfLimpo}";"${emailLimpo}";"${cepLimpo}";"${dataNascFormatada}"`;
        });
        const conteudoCSV = [cabecalhos.join(';'), ...linhasCSV].join('\n');
        const blob = new Blob(['﻿' + conteudoCSV], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Monitoramento_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Meses disponíveis para o filtro de período — extraídos do próprio dado (mais recente primeiro).
    const mesesUnicos = Array.from(new Set(candidatos.map(mesDe).filter(Boolean))).sort().reverse();
    const rotuloMes = (chave) => {
        const [ano, mes] = chave.split('-');
        const texto = new Date(Number(ano), Number(mes) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        return texto.charAt(0).toUpperCase() + texto.slice(1); // "Junho de 2026"
    };

    const vagasUnicas = Array.from(new Set(candidatos.map(c => c.CARGO || c.VAGA_SELECIONADA).filter(Boolean))).sort();
    const opcoesVaga = [
        { label: "Todas as Vagas/Cargos", value: "", isGeral: true },
        ...vagasUnicas.map(vaga => ({ label: vaga, value: vaga, isGeral: true }))
    ];

    // Chips de categoria (filtro de situação, único)
    const categorias = [
        { key: 'TODOS', label: 'Todos', count: totalGeral, cor: '#475569' },
        { key: 'ATIVOS', label: 'Em Processo', count: totalEmProcesso, cor: '#1d4ed8' },
        { key: 'APROVADOS', label: 'Aprovados', count: totalAprovados, cor: '#047857' },
        { key: 'DESISTENTES', label: 'Desistentes', count: totalDesistentes, cor: '#b45309' },
        { key: 'REPROVADOS', label: 'Reprovados', count: totalReprovados, cor: '#be123c' },
    ];

    // Toggles de controle (independentes, on/off)
    const toggles = [
        { label: 'Reincidentes', count: qtdReincidentes, icon: Repeat, ativo: filtroReincidente, set: setFiltroReincidente },
        { label: 'Funcionário atual', count: qtdFuncAtual, icon: UserCheck, ativo: filtroFuncAtual, set: setFiltroFuncAtual },
        { label: 'Ex-funcionário', count: qtdExFunc, icon: UserMinus, ativo: filtroExFunc, set: setFiltroExFunc },
        { label: `Parados +${LIMITE_PARADO_DIAS}d`, count: qtdParados, icon: AlertTriangle, ativo: filtroParado, set: setFiltroParado },
    ];

    const badgeMini = (texto, cor, bg) => (
        <span style={{ backgroundColor: bg, color: cor, padding: '2px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, whiteSpace: 'nowrap' }}>{texto}</span>
    );

    return (
        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '16px' }}>
                <h2 style={estilos.tituloSecao}>Monitoramento de Candidatos</h2>
                <Botao variant="sucesso" icone={Download} onClick={exportarParaCSV}>Exportar Excel</Botao>
            </div>

            {/* ===== FUNIL DE CONVERSÃO (métricas do /metricas, trazidas da Visão Geral) ===== */}
            <p style={{ ...estilos.subtituloSecao, margin: '0 0 10px 0', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.4px', fontSize: '12px' }}>Funil de Conversão</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '8px' }}>
                <CartaoKpi titulo="Acessos (Vagas)" valor={metricas.visitas_vagas} icone={Eye} cor="#6366f1" />
                <CartaoKpi titulo="Acessos (Form)" valor={metricas.visitas_inscricao} icone={FileText} cor="#0ea5e9" />
                <CartaoKpi titulo="Currículos Recebidos" valor={metricas.total_inscricoes} icone={Users} cor="#3b82f6" />
                <CartaoKpi titulo="Aprovados" valor={metricas.total_aprovados} icone={Users} cor="#0baf13" />
                <CartaoKpi titulo="Taxa de Conversão" valor={metricas.taxa_conversao} sufixo="%" icone={TrendingUp} cor="#047857" />
                <CartaoKpi titulo="Reprovados" valor={metricas.taxa_reprovacao} sufixo="%" icone={UserX} cor="#be123c" />
            </div>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 24px 0' }}>
                ℹ️ Currículos Recebidos conta pessoas (uma linha por CPF) — fecha com a soma das situações abaixo. Acessos são rastreados desde 19/03/2026.
            </p>

            {/* ===== SITUAÇÃO (chips clicáveis = filtro de categoria) ===== */}
            <p style={{ ...estilos.subtituloSecao, margin: '0 0 10px 0', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.4px', fontSize: '12px' }}>Situação dos Candidatos</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                {categorias.map(cat => {
                    const ativo = filtroStatus === cat.key;
                    return (
                        <button key={cat.key} onClick={() => setFiltroStatus(cat.key)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                            borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                            border: ativo ? '2px solid #e11d48' : '1px solid #e2e8f0',
                            backgroundColor: ativo ? '#ffe4e6' : '#fff', color: ativo ? '#e11d48' : '#475569', transition: 'all 0.15s',
                        }}>
                            {cat.label}
                            <span style={{ backgroundColor: cat.cor, color: '#fff', borderRadius: '999px', padding: '1px 9px', fontSize: '12px', fontWeight: 900 }}>{cat.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* ===== CONTROLE (toggles independentes) ===== */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {toggles.map((t, i) => (
                    <button key={i} onClick={() => t.set(v => !v)} title="Filtrar (clique para ligar/desligar)" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '7px 12px',
                        borderRadius: '999px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                        border: t.ativo ? '2px solid #e11d48' : '1px dashed #cbd5e1',
                        backgroundColor: t.ativo ? '#ffe4e6' : '#fff', color: t.ativo ? '#e11d48' : '#64748b', transition: 'all 0.15s',
                    }}>
                        <t.icon size={14} /> {t.label}
                        <span style={{ backgroundColor: t.ativo ? '#e11d48' : '#94a3b8', color: '#fff', borderRadius: '999px', padding: '1px 8px', fontSize: '11px', fontWeight: 900 }}>{t.count}</span>
                    </button>
                ))}
            </div>

            {/* ===== FILTROS (texto + vaga) ===== */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px', backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: '1.4', minWidth: '220px', position: 'relative', height: '42px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                        type="text"
                        placeholder="Buscar por nome..."
                        value={buscaNome}
                        onChange={(e) => setBuscaNome(e.target.value)}
                        style={{ width: '100%', height: '100%', boxSizing: 'border-box', padding: '0 10px 0 40px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', outline: 'none', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#cbd5e1'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                </div>
                <DropdownCustomizado
                    value={buscaVaga} onChange={setBuscaVaga} dados={opcoesVaga}
                    placeholder="Todas as Vagas/Cargos" flex="1" minWidth="200px"
                />
                <div style={{ flex: '0 1 200px', minWidth: '170px' }}>
                    <select value={filtroOrigem} onChange={(e) => setFiltroOrigem(e.target.value)} style={{ ...estilos.input, height: '42px', cursor: 'pointer' }}>
                        <option value="">Todas as Origens</option>
                        <option value="PUBLICA">Pública (site)</option>
                        <option value="INTERNA">Interna (RH)</option>
                    </select>
                </div>
                {/* Período (mês/ano) pela data de inscrição */}
                <div style={{ flex: '0 1 200px', minWidth: '170px' }}>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)} style={{ ...estilos.input, height: '42px', cursor: 'pointer' }}>
                        <option value="">Todos os Períodos</option>
                        {mesesUnicos.map(m => <option key={m} value={m}>{rotuloMes(m)}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Carregando dados dos candidatos...</div>
            ) : (
                <div style={estilos.tabelaWrap}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={estilos.tabela}>
                            <thead>
                                <tr>
                                    <th onClick={() => lidarComOrdenacao('INSCRICAO')} style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Inscrição {renderizarIconeOrdenacao('INSCRICAO')}</div>
                                    </th>
                                    <th onClick={() => lidarComOrdenacao('NOME')} style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Candidato {renderizarIconeOrdenacao('NOME')}</div>
                                    </th>
                                    <th onClick={() => lidarComOrdenacao('VAGA')} style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Vaga/Cargo {renderizarIconeOrdenacao('VAGA')}</div>
                                    </th>
                                    <th onClick={() => lidarComOrdenacao('PARTICIPACOES')} style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>Participações {renderizarIconeOrdenacao('PARTICIPACOES')}</div>
                                    </th>
                                    <th onClick={() => lidarComOrdenacao('STATUS')} style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Status Detalhado {renderizarIconeOrdenacao('STATUS')}</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidatosPaginados.map((cand, idx) => {
                                    const infoStatus = getStatusDisplay(cand);
                                    const qtd = Number(cand.QTD_PARTICIPACOES) || 1;
                                    const parado = isParado(cand);
                                    return (
                                        <tr key={`${cand.CPF || 'nocpf'}-${cand.ORIGEM}-${cand.ID}-${idx}`} style={{ transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                            <td style={{ ...estilos.td, color: CORES.textoSec, whiteSpace: 'nowrap' }}>{fmtData(cand.DATA_INSCRICAO)}</td>
                                            <td style={estilos.td}>
                                                <div style={{ fontWeight: 600, color: CORES.texto }}>{cand.NOME_COMPLETO}</div>
                                                {(isFuncAtual(cand) || isExFunc(cand) || parado || origemDe(cand) === 'INTERNA') && (
                                                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                        {origemDe(cand) === 'INTERNA' && badgeMini('Interna (RH)', '#6d28d9', '#f5f3ff')}
                                                        {isFuncAtual(cand) && badgeMini('Funcionário atual', '#1d4ed8', '#eff6ff')}
                                                        {/* Cinza-ardósia de propósito: âmbar já é o "Parado +Xd" logo ao lado, e vínculo encerrado lê melhor em tom neutro. */}
                                                        {isExFunc(cand) && badgeMini('Ex-funcionário', '#475569', '#f1f5f9')}
                                                        {parado && badgeMini(`Parado ${diasNoFunil(cand)}d`, '#b45309', '#fffbeb')}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ ...estilos.td, color: CORES.textoSec }}>{cand.CARGO || cand.VAGA_SELECIONADA || 'Não definida'}</td>
                                            <td style={{ ...estilos.td, textAlign: 'center' }}>
                                                {qtd > 1
                                                    ? <span title={`Este CPF se inscreveu ${qtd} vezes`} style={{ backgroundColor: '#ffe4e6', color: '#e11d48', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 800 }}>{qtd}x</span>
                                                    : <span style={{ color: CORES.textoFraco }}>{qtd}</span>}
                                            </td>
                                            <td style={estilos.td}>
                                                {/* Badge clicável = link rápido para a aba onde o candidato está sendo tratado */}
                                                <button
                                                    onClick={() => irParaAba && irParaAba(abaDoCandidato(cand))}
                                                    title="Ir para a tela deste candidato"
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', backgroundColor: infoStatus.bg, color: infoStatus.cor, padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap', border: 'none', cursor: irParaAba ? 'pointer' : 'default' }}
                                                >
                                                    {infoStatus.texto}
                                                    {irParaAba && <ExternalLink size={12} />}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPaginas > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <span style={{ fontSize: '14px', color: '#64748b' }}>
                                Mostrando {indexPrimeiro + 1} a {Math.min(indexUltimo, candidatosOrdenados.length)} de {candidatosOrdenados.length} candidatos
                            </span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))} disabled={paginaAtual === 1} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: paginaAtual === 1 ? '#f1f5f9' : '#fff', color: paginaAtual === 1 ? '#cbd5e1' : '#0f172a', cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                    <ChevronLeft size={18} />
                                </button>
                                <span style={{ padding: '8px 16px', fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>Página {paginaAtual} de {totalPaginas}</span>
                                <button onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))} disabled={paginaAtual === totalPaginas} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: paginaAtual === totalPaginas ? '#f1f5f9' : '#fff', color: paginaAtual === totalPaginas ? '#cbd5e1' : '#0f172a', cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}

                    {candidatosOrdenados.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Nenhum candidato encontrado com os filtros atuais.</div>}
                </div>
            )}
        </div>
    );
};

export default ProcessoSeletivoMonitoramento;
